export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  tenant_id: number;
  /** Company/tenant display name returned by /api/v1/auth/me/ */
  tenant_name?: string;
  avatar_url?: string | null;
  last_login?: string | null;
  /** Whether the user has multi-factor authentication (TOTP) enabled. */
  mfa_enabled: boolean;
  /**
   * Sprint 228 — the tenant has at least one active, unexpired Public API key.
   * Sent by `GET/PATCH /api/v1/auth/me/` ONLY (MeSerializer), never by login, so
   * a freshly logged-in session does not have it until `/me` is read — see
   * `usePublicApiEnabled`.
   */
  public_api_enabled?: boolean;
}

/** Successful login — tokens + user identity are issued immediately. */
export interface LoginSuccess {
  access: string;
  refresh: string;
  user: User;
}

/**
 * MFA challenge — login credentials were valid but the account has MFA
 * enabled. No auth tokens are issued; the interim `mfa_token` must be
 * exchanged (together with a TOTP/recovery code) at `mfa/verify/`.
 */
export interface LoginMfaChallenge {
  mfa_required: true;
  mfa_token: string;
}

/** The login endpoint returns EITHER a success payload OR an MFA challenge. */
export type LoginResponse = LoginSuccess | LoginMfaChallenge;

/** Narrows a LoginResponse to the MFA-challenge branch. */
export function isMfaChallenge(
  response: LoginResponse,
): response is LoginMfaChallenge {
  return "mfa_required" in response && response.mfa_required === true;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
}
